import { useEffect, useState } from 'react';
import type { BlogPost, WhatsNewContent } from 'venn-api';
import {
  getSponsoredContent,
  updateSponsoredContent,
  deleteSponsoredContent,
  getLatestBlogPostIfAvailable,
} from 'venn-api';
import { logExceptionIntoSentry, analyticsService } from 'venn-utils';

const toWhatsNewContent = (blogPost: BlogPost): WhatsNewContent => ({
  title: blogPost.html_title,
  text: blogPost.meta_description,
  url: blogPost.url,
  link: 'Learn more',
  publishedAt: blogPost.publish_date,
  sponsored: false,
});

const useWhatsNewSectionData = (sponsorAccountId: number) => {
  const [isDefault, setIsDefault] = useState(true);
  const [whatsNewData, setWhatsNewData] = useState<WhatsNewContent>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSponsoredContent = async () => {
      try {
        const {
          content: { whatsNewContent },
        } = await getSponsoredContent();

        if (whatsNewContent) {
          setWhatsNewData(whatsNewContent);
          setIsLoading(false);
          setIsDefault(false);
          return;
        }

        const blogPost = await getLatestBlogPostIfAvailable();
        setWhatsNewData(blogPost ? toWhatsNewContent(blogPost) : undefined);
        setIsLoading(false);
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        logExceptionIntoSentry(e);
        setIsLoading(false);
      }
    };
    fetchSponsoredContent();
  }, []);

  const handleOnEdit = async (data: WhatsNewContent) => {
    try {
      setIsLoading(true);
      const {
        content: { whatsNewContent },
      } = await updateSponsoredContent({ whatsNewContent: data });
      const parsedContent = {
        title: whatsNewContent.title,
        text: whatsNewContent.text,
        url: whatsNewContent.url,
        link: whatsNewContent.link,
        publishedAt: whatsNewContent.publishedAt,
        sponsored: whatsNewContent.sponsored,
      };
      analyticsService.homeContentUpdated({ sponsorAccountId, type: 'update' });
      setWhatsNewData(parsedContent);
      setIsDefault(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleOnReset = async () => {
    try {
      setIsLoading(true);
      await deleteSponsoredContent();
      const blogPost = await getLatestBlogPostIfAvailable();
      analyticsService.homeContentUpdated({ sponsorAccountId, type: 'reset' });
      setWhatsNewData(blogPost ? toWhatsNewContent(blogPost) : undefined);
      setIsDefault(true);
      setIsLoading(false);
    } catch (error) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(error);
      setIsLoading(false);
    }
  };

  return { isDefault, whatsNewData, isLoading, handleOnEdit, handleOnReset };
};

export default useWhatsNewSectionData;
